import * as Sentry from '@sentry/react';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useGoogleMaps } from './GoogleMapsProvider';
import { Input } from './ui/input';
import { MapPin, Loader2, Check } from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * AddressAutocomplete - Google Places address autocomplete with geocoding
 * 
 * This component provides address autocomplete using Google Places API
 * and automatically geocodes the selected/entered address.
 */
export function AddressAutocomplete({
  value = '',
  onChange,
  onAddressSelect,
  placeholder = 'Rechercher une adresse...',
  className = '',
  disabled = false,
  ...props
}) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const { isLoaded, error } = useGoogleMaps();
  const [inputValue, setInputValue] = useState(value);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [isGeocoded, setIsGeocoded] = useState(false);
  
  // Use refs to store callbacks to avoid stale closures
  const onChangeRef = useRef(onChange);
  const onAddressSelectRef = useRef(onAddressSelect);
  
  // Keep refs updated
  useEffect(() => {
    onChangeRef.current = onChange;
    onAddressSelectRef.current = onAddressSelect;
  }, [onChange, onAddressSelect]);

  // Sync input value with external value
  useEffect(() => {
    setInputValue(value);
    // Check if value looks like it's already formatted
    if (value && value.includes(', ')) {
      setIsGeocoded(true);
    }
  }, [value]);

  // Geocode an address string and return coordinates
  const geocodeAddress = useCallback(async (address) => {
    if (!window.google || !address || address.length < 5) return null;
    
    return new Promise((resolve) => {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: address }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          const result = results[0];
          const location = result.geometry.location;
          
          // Parse address components
          const addressData = {
            formattedAddress: result.formatted_address,
            lat: location.lat(),
            lng: location.lng(),
            placeId: result.place_id,
          };
          
          // Extract city and postal code from components
          result.address_components.forEach((component) => {
            if (component.types.includes('locality')) {
              addressData.city = component.long_name;
            }
            if (component.types.includes('postal_code')) {
              addressData.postalCode = component.long_name;
            }
          });
          
          resolve(addressData);
        } else {
          resolve(null);
        }
      });
    });
  }, []);

  // Initialize Google Places Autocomplete
  useEffect(() => {
    if (!isLoaded || !inputRef.current || autocompleteRef.current) return;

    // Ensure the Places library and Autocomplete class are available
    if (!window.google?.maps?.places?.Autocomplete) {
      return;
    }

    try {
      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        inputRef.current,
        {
          types: ['address'],
          componentRestrictions: { country: 'fr' },
          fields: ['address_components', 'formatted_address', 'geometry', 'place_id'],
        }
      );

      // Fix z-index for dialogs
      const pacContainerObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.classList && node.classList.contains('pac-container')) {
              node.style.zIndex = '99999';
              if (node.parentElement !== document.body) {
                document.body.appendChild(node);
              }
            }
          });
        });
      });
      pacContainerObserver.observe(document.body, { childList: true, subtree: true });

      // Handle place selection
      autocompleteRef.current.addListener('place_changed', async () => {
        const place = autocompleteRef.current.getPlace();
        
        if (!place.geometry) {
          // User pressed enter without selecting - geocode the input
          return;
        }

        const formattedAddress = place.formatted_address;
        
        // Update local state
        setInputValue(formattedAddress);
        
        // Call onChange with the formatted address
        if (onChangeRef.current) {
          onChangeRef.current(formattedAddress);
        }
        
        // Build address data
        const addressData = {
          formattedAddress,
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
          placeId: place.place_id,
        };
        
        // Extract additional components
        if (place.address_components) {
          place.address_components.forEach((component) => {
            if (component.types.includes('street_number')) {
              addressData.streetNumber = component.long_name;
            }
            if (component.types.includes('route')) {
              addressData.route = component.long_name;
            }
            if (component.types.includes('locality')) {
              addressData.city = component.long_name;
            }
            if (component.types.includes('postal_code')) {
              addressData.postalCode = component.long_name;
            }
          });
          addressData.address = [addressData.streetNumber, addressData.route].filter(Boolean).join(' ');
        }
        
        
        // Mark as geocoded
        setIsGeocoded(true);
        
        if (onAddressSelectRef.current) {
          onAddressSelectRef.current(addressData);
        }
      });

      return () => {
        if (autocompleteRef.current) {
          window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        }
        pacContainerObserver.disconnect();
      };
    } catch (err) {
      Sentry.captureException(err);
    }
  }, [isLoaded]);

  // Handle input change (user typing)
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setIsGeocoded(false); // Reset geocoded status when typing
    
    // Call onChange to update parent - this ensures the typed value is saved
    if (onChangeRef.current) {
      onChangeRef.current(newValue);
    }
  };

  // Handle blur - geocode the address to format it and get coordinates
  const handleBlur = async () => {
    // Skip if already geocoded (user selected from dropdown)
    if (isGeocoded) return;
    
    const currentValue = inputValue?.trim();
    
    // Skip if no value or too short
    if (!currentValue || currentValue.length < 5) return;
    
    // Skip if it looks like already formatted (contains comma + "France")
    if (currentValue.includes(', France')) return;
    
    setIsGeocoding(true);
    
    try {
      const addressData = await geocodeAddress(currentValue);
      
      if (addressData) {
        
        // Update with formatted address
        setInputValue(addressData.formattedAddress);
        setIsGeocoded(true);
        
        if (onChangeRef.current) {
          onChangeRef.current(addressData.formattedAddress);
        }
        
        if (onAddressSelectRef.current) {
          onAddressSelectRef.current(addressData);
        }
      }
    } catch (err) {
      Sentry.captureException(err);
    } finally {
      setIsGeocoding(false);
    }
  };

  // Fallback: plain usable input when Maps unavailable (no key or load error)
  if (error) {
    return (
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none text-muted-foreground" />
        <Input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          placeholder={placeholder}
          disabled={disabled}
          className={cn("pl-9", className)}
          autoComplete="off"
          {...props}
        />
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="relative">
        <MapPin className={cn(
          "absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none",
          isGeocoded ? "text-green-500" : "text-muted-foreground"
        )} />
        <Input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            "pl-9 pr-8",
            isGeocoding && "pr-10",
            isGeocoded && "border-green-200",
            className
          )}
          autoComplete="off"
          {...props}
        />
        {isGeocoding && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
        )}
        {isGeocoded && !isGeocoding && (
          <Check className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />
        )}
      </div>
    </div>
  );
}

export default AddressAutocomplete;
